import React, { useMemo, useState } from "react";
import "./faq-section.css";

/**
 * Usage:
 * <FaqSection />
 * or
 * <FaqSection items={[ ...customItems ]} />
 */

function ToggleButton({ open }) {
  return (
    <span className="slFaq-toggle" aria-hidden="true">
      <span
        className={`slFaq-toggleIcon ${open ? "isOpen" : ""}`}
        aria-hidden="true"
      >
        +
      </span>
    </span>
  );
}

function BulletRow({ icon, title, children }) {
  return (
    <div className="slFaq-bulletRow">
      <div className="slFaq-bulletIcon" aria-hidden="true">
        {icon}
      </div>
      <div className="slFaq-bulletText">
        {title ? <strong>{title}: </strong> : null}
        {children}
      </div>
    </div>
  );
}

function Callout({ tone = "info", children }) {
  return (
    <div className={`slFaq-callout slFaq-callout--${tone}`}>{children}</div>
  );
}

function Chips({ items = [] }) {
  if (!items.length) return null;
  return (
    <div className="slFaq-chips">
      {items.map((c, i) => (
        <span
          key={i}
          className={`slFaq-chip slFaq-chip--${c.tone || "neutral"}`}
        >
          <span className="slFaq-chipIcon" aria-hidden="true">
            {c.icon}
          </span>
          <span>{c.label}</span>
        </span>
      ))}
    </div>
  );
}

function FaqItem({ item, open, onToggle }) {
  return (
    <div className={`slFaq-card ${open ? "isOpen" : ""}`}>
      <button type="button" className="slFaq-header" onClick={onToggle} aria-expanded={open}>
        <div className="slFaq-headerLeft">
          <h3 className="slFaq-q">{item.question}</h3>
        </div>
        <ToggleButton open={open} />
      </button>

      {open ? (
        <div className="slFaq-body">
          {item.summary ? (
            <div className="slFaq-summary">{item.summary}</div>
          ) : null}
          {item.content}
        </div>
      ) : null}
    </div>
  );
}

export default function FaqSection({
  items,
  subtitle = "Everything you need to know about applying for a P2P corporate loan on SmartLend.",
}) {
  const [openSet, setOpenSet] = useState(() => new Set([0])); // first open by default

  const defaultItems = useMemo(
    () => [
      {
        showSparkle: true,
        question:
          "What's the difference between applying through SmartLend vs a loan broker?",
        summary: "No broker fees · Full transparency · You stay in control",
        content: (
          <>
            <p>
              Loan brokers act as intermediaries who approach lenders on your
              behalf — and typically charge{" "}
              <strong>3%–8% of the loan amount</strong> as a referral or
              arrangement fee, sometimes on top of the lender's own processing
              fee. In some instances, we've even heard of broker fees reaching{" "}
              <strong>20%–30% of the loan amount</strong>.
            </p>

            <p>
              With SmartLend, there is{" "}
              <strong>no broker, no middleman, and no referral fee</strong>.
              Your request goes directly to our full lender network
              simultaneously. You see every offer, compare them side by side,
              and choose entirely on your own terms.
            </p>

            <div className="slFaq-bullets">
              <BulletRow icon="🚫" title="Broker">
                Charges fees, controls which lenders you see, may favour certain
                lenders for commissions.
              </BulletRow>
              <BulletRow icon="✅" title="SmartLend">
                Zero fees, full lender network access, complete transparency on
                every offer.
              </BulletRow>
            </div>

            <p>
              In fact, SmartLend works no differently from engaging a loan
              broker — except we likely have the{" "}
              <strong>same or wider lender network</strong>. And because of our
              sheer volume, we’re able to launch{" "}
              <strong>
                exclusive campaigns with reduced interest rates and processing
                fees
              </strong>{" "}
              specially for SmartLend borrowers — something a typical broker
              simply cannot offer.
            </p>
          </>
        ),
      },
      {
        showSparkle: true,
        question: "SmartLend is free for borrowers — so how do you earn?",
        summary:
          "Free for borrowers · Revenue comes from lenders · No conflict of interest",
        content: (
          <>
            <p>
              SmartLend is completely free for borrowers —{" "}
              <strong>
                no platform fees, no broker fees, no hidden charges
              </strong>
              .
            </p>
            <p>
              We earn a small referral or listing fee from the{" "}
              <strong>lenders</strong> when a loan is successfully matched and
              disbursed. This means our incentive is always to get you the best
              possible outcome — the better the match, the better it is for
              everyone.
            </p>

            <Callout tone="info">
              <strong>ℹ️</strong> Because we earn from lenders — not borrowers —
              there is <strong>no conflict of interest</strong> in which offers
              we show you. You always see the full picture.
            </Callout>

            <p>
              A common concern is whether our referral fee translates into
              higher interest rates or borrowing costs for you. The answer is{" "}
              <strong>no</strong>. Our referral fee is a standard cost of lender
              acquisition — similar to how any business accounts for marketing
              or distribution costs. Lenders factor this into their operating
              model, <strong>not into the rate they offer you</strong>.
            </p>

            <p>
              In fact, because multiple lenders are competing for your business
              simultaneously on SmartLend, the natural effect is{" "}
              <strong>downward pressure on rates</strong> — meaning you’re more
              likely to get a better deal here than going to a single lender
              directly or through a broker who may not show you all available
              options.
            </p>
          </>
        ),
      },
      {
        showSparkle: false,
        question:
          "My bank rejected my loan application. Can I still apply here?",
        summary: "Yes — P2P lenders use different criteria than banks",
        content: (
          <>
            <p>
              A bank rejection doesn't disqualify you from P2P corporate
              lending. <strong>P2P lenders assess viability differently</strong>{" "}
              — they consider softer signals like business activity, cash flow
              consistency, and industry context, not just credit scores and
              collateral.
            </p>

            <div className="slFaq-bullets">
              <BulletRow icon="🏦">
                Banks typically require 2+ years of operation, audited
                financials, and strong CBS scores.
              </BulletRow>
              <BulletRow icon="✅">
                P2P lenders are more flexible — businesses{" "}
                <strong>under 2 years old</strong> or with{" "}
                <strong>thinner credit history</strong> can still qualify.
              </BulletRow>
              <BulletRow icon="🔄">
                SmartLend submits your request to{" "}
                <strong>multiple lenders at once</strong> — even if some pass,
                others may still make an offer.
              </BulletRow>
            </div>

            <Callout tone="info">
              <strong>ℹ️</strong> P2P lending and bank loans are{" "}
              <strong>complementary</strong>, not competing. Many businesses use
              P2P for short-term working capital while building the credit
              history needed for future bank facilities.
            </Callout>
          </>
        ),
      },
      {
        showSparkle: false,
        question:
          "Is SmartLend regulated? Are the lenders on the platform licensed?",
        summary:
          "All partner lenders are MAS-regulated · Licensed P2P platforms under Capital Markets Services licence",
        content: (
          <>
            <p>
              Yes. All P2P corporate lenders on SmartLend's network are licensed
              and regulated under one of two frameworks:
            </p>

            <div className="slFaq-bullets">
              <BulletRow icon="🏛️">
                Licensed by the{" "}
                <strong>Monetary Authority of Singapore (MAS)</strong> under the
                Securities and Futures Act (SFA), requiring a Capital Markets
                Services (CMS) licence.
              </BulletRow>
              <BulletRow icon="📜">
                Operating as an <strong>Excluded Moneylender</strong> under the
                Moneylenders Act — lenders who lend solely to corporations are
                exempt from holding a moneylending licence, as corporate lending
                is de-regulated under Singapore law.
              </BulletRow>
              <BulletRow icon="🛡️">
                MAS-licensed platforms must conduct{" "}
                <strong>proper due diligence</strong> on borrowers and disclose
                this to investors.
              </BulletRow>
              <BulletRow icon="🧾">
                Interest rates and fees must be disclosed{" "}
                <strong>transparently and consistently</strong> across all loan
                listings.
              </BulletRow>
              <BulletRow icon="🧯">
                Anti-money laundering (AML) and counter-terrorism financing
                (CTF) measures are mandatory for all platforms.
              </BulletRow>
            </div>

            <Callout tone="info">
              <strong>ℹ️</strong> SmartLend is a{" "}
              <strong>loan matching marketplace</strong> — we connect you to
              licensed lenders but are not a lender ourselves. We do not collect
              or hold any loan funds.
            </Callout>
          </>
        ),
      },
      {
        showSparkle: false,
        question: "How fast can I get an offer? When will funds be disbursed?",
        summary:
          "Offers within 24 hours · Disbursement typically within 1–3 business days after acceptance",
        content: (
          <>
            <div className="slFaq-bullets">
              <BulletRow icon="⚡">
                Once your application is submitted, lenders typically respond
                with offers within <strong>24–48 hours</strong>.
              </BulletRow>
              <BulletRow icon="📝">
                After you <strong>accept an offer</strong>, the lender will
                conduct final verification and prepare the loan agreement.
              </BulletRow>
              <BulletRow icon="💸">
                Funds are typically disbursed within{" "}
                <strong>1–2 business days</strong> after signing — significantly
                faster than the 3–5 weeks typical of bank loans.
              </BulletRow>
            </div>

            <Callout tone="warn">
              <strong>⚠️</strong> Requests above <strong>S$500,000</strong>{" "}
              require manual vetting by our team before going live on the
              marketplace. This may add <strong>1–2 business days</strong> to
              your timeline.
            </Callout>
          </>
        ),
      },
      {
        showSparkle: false,
        question: "What interest rates and fees should I expect?",
        summary:
          "Interest: 1%–4% p.m. · Processing fee: 1%–10% · No platform or broker fees on SmartLend",
        content: (
          <>
            <div className="slFaq-bullets">
              <BulletRow icon="📈" title="Interest rate">
                Typically 1%–4% per month on a simple interest basis. Your
                actual rate depends on your credit profile, loan amount, and
                tenor.
              </BulletRow>
              <BulletRow icon="🧾" title="Processing/admin fee">
                One-time fee of 3%–8% of the principal, deducted upfront by the
                lender.
              </BulletRow>
              <BulletRow icon="⏱️" title="Early repayment fee">
                Some lenders charge a fee (typically 1% of remaining principal)
                if you repay ahead of schedule. Always check your loan
                agreement.
              </BulletRow>
              <BulletRow icon="🚫" title="SmartLend platform fee">
                Zero. We charge no broker, referral, or platform fees — ever.
              </BulletRow>
            </div>

            <Callout tone="info">
              <strong>ℹ️</strong> Choosing a lower interest rate preference
              (e.g. 1%–2% p.m.) may{" "}
              <strong>
                reduce the number of lenders willing to make an offer
              </strong>
              . A slightly higher range typically attracts more competing bids.
            </Callout>
          </>
        ),
      },
      {
        showSparkle: false,
        question: "Will applying affect my future bank loan application?",
        summary:
          "No impact on your CBS · Reflected as self-enquiry · Bank applications unaffected",
        content: (
          <>
            <p>
              No, it won't. Since the CBS report is{" "}
              <strong>retrieved by you</strong> using your own access code — and
              our lenders are not banks or financial institutions — the enquiry
              is recorded as a <strong>self-enquiry on your CBS</strong>.
            </p>
            <p>
              This means there will be{" "}
              <strong>no multiple enquiry trail</strong> on your CBS report, and
              your future bank loan applications remain completely unaffected.
            </p>

            <Chips
              items={[
                { icon: "✓", label: "No hard enquiry on CBS", tone: "success" },
                {
                  icon: "i",
                  label: "Reflected as self-enquiry only",
                  tone: "info",
                },
              ]}
            />
          </>
        ),
      },
      {
        showSparkle: false,
        question:
          "Will applying affect my personal or business credit score (CBS / MLCB)?",
        summary: "No impact · Our lenders do not report to CBS or MLCB",
        content: (
          <>
            <p>
              It won't. Our P2P corporate lenders{" "}
              <strong>do not report to CBS or MLCB</strong>, so applying or
              drawing down a loan through SmartLend will not appear on your
              credit bureau records and will not negatively affect your credit
              score.
            </p>

            <Callout tone="warn">
              <strong>⚠️ Note:</strong> Some lenders may request your MLCB
              report as part of their own credit review process.
            </Callout>
          </>
        ),
      },
      {
        showSparkle: false,
        question:
          "Is my company eligible to apply? What are the basic requirements?",
        summary: "Singapore-registered Pte. Ltd. · Min. 6 months in operation",
        content: (
          <>
            <div className="slFaq-bullets">
              <BulletRow icon="🏢">
                Business must be a <strong>Private Limited (Pte. Ltd.)</strong>{" "}
                company, registered and operating in Singapore.
              </BulletRow>
              <BulletRow icon="📅">
                Minimum <strong>6 months</strong> of operating history from date
                of incorporation.
              </BulletRow>
            </div>
          </>
        ),
      },
      {
        showSparkle: false,
        question: "What documents do I need to prepare?",
        summary:
          "Singpass login · Last 6 months bank statements · Free CBS report (access code provided)",
        content: (
          <>
            <p>
              Thanks to our <strong>Singpass integration</strong>, the entire
              application is just 3 simple steps — no lengthy paperwork, no
              back-and-forth.
            </p>

            <div className="slFaq-bullets">
              <BulletRow icon="🔐" title="Log in via Singpass">
                Your personal and business details are auto-filled instantly.
              </BulletRow>
              <BulletRow icon="💰" title="Last 6 months of bank statements">
                Upload directly for lender assessment.
              </BulletRow>
              <BulletRow icon="📘" title="CBS credit report">
                During the application flow, you will be given a{" "}
                <strong>free access code</strong> to log in to CBS and retrieve
                your report directly. No cost to you.
              </BulletRow>
            </div>

            <Callout tone="info">
              <strong>ℹ️</strong> And that's it —{" "}
              <strong>simple as that.</strong> No financial statements, no NOA,
              no lengthy forms at the application stage. Our lenders do the
              rest.
            </Callout>

            <Callout tone="warn">
              <strong>⚠️ Please note:</strong> Once a loan is approved,
              individual lenders may request hard copies of documents such as
              your NOA, NRIC, or other KYC materials as part of their own
              internal compliance process. This is standard practice and varies
              by lender.
            </Callout>
          </>
        ),
      },
    ],
    [],
  );

  const data = items && items.length ? items : defaultItems;

  const toggle = (idx) => {
    setOpenSet((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  return (
    <section className="slFaq-section">
      <div className="slFaq-head">
        <div className="slFaq-eyebrow">Got questions?</div>
        <h2 className="slFaq-title">Frequently Asked Questions</h2>
        <p className="slFaq-subtitle">
          {subtitle}
        </p>
      </div>

      <div className="slFaq-list">
        {data.map((item, idx) => (
          <FaqItem
            key={idx}
            item={item}
            open={openSet.has(idx)}
            onToggle={() => toggle(idx)}
          />
        ))}
      </div>
    </section>
  );
}
